// WebAuthn (Face ID / biometric) helper functions
// Credential ID stored in sessionStorage (cleared on tab close) with obfuscation
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CRED_KEY = "mp_wac";

/**
 * Check if WebAuthn is supported on this device/browser.
 */
export async function isWebAuthnSupported() {
  if (!window.PublicKeyCredential) return false;
  try {
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}

/**
 * Get stored credential ID. Checks sessionStorage first, falls back to
 * a server-side check via cookie-based session.
 */
export function getStoredCredentialId() {
  try {
    const encoded = sessionStorage.getItem(CRED_KEY);
    if (encoded) return atob(encoded);
  } catch {
    // sessionStorage not available
  }
  return null;
}

/**
 * Store credential ID in sessionStorage (base64 obfuscated).
 * Also persists a flag cookie so the landing page knows to offer biometric.
 */
function setStoredCredentialId(credentialId) {
  try {
    sessionStorage.setItem(CRED_KEY, btoa(credentialId));
    // Set a non-sensitive flag cookie (no credential data) for cross-session hint
    document.cookie = `mp_has_bio=1;path=/;max-age=${60 * 60 * 24 * 365};SameSite=Strict;Secure`;
  } catch {
    // sessionStorage not available
  }
}

/**
 * Clear stored credential on removal/disable.
 */
export function clearStoredCredential() {
  try {
    sessionStorage.removeItem(CRED_KEY);
    document.cookie = "mp_has_bio=;path=/;max-age=0;SameSite=Strict;Secure";
  } catch {
    // ignore
  }
}

/**
 * Check if biometric was previously enrolled (cross-session, via cookie flag).
 */
export function hasBiometricHint() {
  return document.cookie.includes("mp_has_bio=1");
}

// Helper: base64url encode ArrayBuffer
function bufferToBase64url(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  // Base64url: swap +/, then strip trailing '=' padding. Manual trim
  // instead of a `=+$` regex — Sonar S8786 flags the quantifier as
  // potentially super-linear on adversarial input; the loop is O(n)
  // with n ≤ 2 in every real base64 output.
  let out = btoa(binary).replace(/\+/g, "-").replace(/\//g, "_");
  while (out.endsWith("=")) out = out.slice(0, -1);
  return out;
}

// Helper: base64url decode to ArrayBuffer
function base64urlToBuffer(base64url) {
  const base64 = base64url.replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

/**
 * Register a new WebAuthn credential (Face ID / fingerprint).
 * Must be called while the user is authenticated.
 */
export async function registerBiometric() {
  // 1. Get registration options from server
  const optionsRes = await axios.get(`${API}/webauthn/register/options`, {
    withCredentials: true,
  });
  const options = optionsRes.data;

  // 2. Convert server options to browser format
  const publicKeyOptions = {
    challenge: base64urlToBuffer(options.challenge),
    rp: options.rp,
    user: {
      ...options.user,
      id: base64urlToBuffer(options.user.id),
    },
    pubKeyCredParams: options.pubKeyCredParams,
    timeout: options.timeout || 60000,
    authenticatorSelection: options.authenticatorSelection,
    attestation: options.attestation || "none",
    excludeCredentials: (options.excludeCredentials || []).map((c) => ({
      ...c,
      id: base64urlToBuffer(c.id),
    })),
  };

  // 3. Create credential (triggers Face ID / biometric prompt)
  const credential = await navigator.credentials.create({
    publicKey: publicKeyOptions,
  });

  // 4. Send response to server for verification
  const body = {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    type: credential.type,
    response: {
      attestationObject: bufferToBase64url(
        credential.response.attestationObject
      ),
      clientDataJSON: bufferToBase64url(credential.response.clientDataJSON),
    },
    authenticatorAttachment: credential.authenticatorAttachment,
  };

  const verifyRes = await axios.post(
    `${API}/webauthn/register/complete`,
    body,
    { withCredentials: true }
  );

  // 5. Store credential ID locally for fast re-login
  if (verifyRes.data.credential_id) {
    setStoredCredentialId(verifyRes.data.credential_id);
  }

  return verifyRes.data;
}

/**
 * Authenticate with stored biometric credential (Face ID / fingerprint).
 * Does NOT require existing session — this creates a new session.
 */
export async function loginWithBiometric() {
  const credentialId = getStoredCredentialId();
  if (!credentialId) {
    throw new Error("No biometric credential found on this device");
  }

  // 1. Get authentication options
  const optionsRes = await axios.post(
    `${API}/webauthn/login/options`,
    { credential_id: credentialId },
    { withCredentials: true }
  );
  const options = optionsRes.data;

  // 2. Convert to browser format
  const publicKeyOptions = {
    challenge: base64urlToBuffer(options.challenge),
    timeout: options.timeout || 60000,
    rpId: options.rpId,
    userVerification: options.userVerification || "required",
    allowCredentials: (options.allowCredentials || []).map((c) => ({
      ...c,
      id: base64urlToBuffer(c.id),
    })),
  };

  // 3. Get assertion (triggers Face ID / biometric prompt)
  const assertion = await navigator.credentials.get({
    publicKey: publicKeyOptions,
  });

  // 4. Send to server for verification
  const body = {
    id: assertion.id,
    rawId: bufferToBase64url(assertion.rawId),
    type: assertion.type,
    response: {
      authenticatorData: bufferToBase64url(
        assertion.response.authenticatorData
      ),
      clientDataJSON: bufferToBase64url(assertion.response.clientDataJSON),
      signature: bufferToBase64url(assertion.response.signature),
      userHandle: assertion.response.userHandle
        ? bufferToBase64url(assertion.response.userHandle)
        : null,
    },
    authenticatorAttachment: assertion.authenticatorAttachment,
  };

  const verifyRes = await axios.post(
    `${API}/webauthn/login/complete`,
    body,
    { withCredentials: true }
  );

  return verifyRes.data;
}
